/**
 * useLiveTicks — WebSocket hook for real-time LTP streaming.
 *
 * Connects to the backend WebSocket at /ws/ticks which bridges Kite's KiteTicker
 * stream to the frontend. Only open positions are subscribed.
 *
 * The WebSocket base URL is derived from VITE_API_BASE_URL:
 *   http://host/api  →  ws://host
 *   https://host/api →  wss://host
 * Falls back to ws://localhost:8000 if VITE_API_BASE_URL is not set.
 *
 * Returns:
 *   ltpMap: Record<symbol, ltp>   — override portfolio LTPs with these
 *   status: "connecting" | "live" | "error" | "closed"
 *   subscribedSymbols: string[]
 *
 * Usage:
 *   const { ltpMap, status } = useLiveTicks();
 *   const ltp = ltpMap[position.symbol] ?? position.ltp;
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { handleStateMessage, onStateChannelDisconnect, onStateChannelReconnect } from "@/state/ws/stateChannel";  // ADR-012 Phase 1 + Phase E

type TickStatus = "connecting" | "live" | "error" | "closed";

export interface TickEntry {
  symbol: string;
  ltp: number;
  // Prediction fields — present only when predictor is tracking this position
  direction?: "UP" | "DOWN" | "NEUTRAL";
  confidence?: number;
  momentum_pct?: number;
  ema5?: number;
  ema20?: number;
  pnl_pct?: number;
  signal?: "HOLD" | "CLOSE";
  reason?: string;
  trailing_stop?: number;
  trail_phase?: "normal" | "breakeven" | "momentum";
}

export interface AutoCloseEvent {
  symbol: string;
  ltp: number;
  reason: string;
  pnl_pct: number;
  pnl_abs: number;
  order_id: string;
  timestamp: number;
}

interface TickMessage {
  type: "tick";
  data: TickEntry[];
}

interface SubscribedMessage {
  type: "subscribed";
  symbols: string[];
  count?: number;
  message?: string;
}

interface AutoCloseMessage {
  type: "auto_close";
  symbol: string;
  ltp: number;
  reason: string;
  pnl_pct: number;
  pnl_abs: number;
  order_id: string;
}

interface HoldConvertMessage {
  type: "hold_convert";
  symbol: string;
  ltp: number;
  product: string;
  reason: string;
  pnl_pct: number;
}

export interface ScanNearMiss {
  symbol: string;
  score: number;
  confluence: number;
  filter: string;
  reason: string;
}

export interface ScanStats {
  total_liquid: number;
  hold: number;
  score_low: number;
  time_miss: number;
  profit_low: number;
  duplicate: number;
  auto_executed: number;
  queued: number;
  near_misses: ScanNearMiss[];
}

export interface ScanRegime {
  adx: number;
  is_trending: boolean;
  direction: string;
}

export interface ScanDrawdown {
  today_pnl_pct: number;
  triggered: boolean;
  effective_limit_pct: number;
  position_count: number;
  position_cap: number;
}

export interface ScanCompleteEvent {
  auto_trade: boolean;
  capital_used_pct: number;
  capital_limit_pct: number;
  capital_limit_reached: boolean;
  auto_executed: number;
  pending_count: number;
  stats?: ScanStats;
  regime?: ScanRegime;
  drawdown?: ScanDrawdown;
}

interface ScanCompleteMessage extends ScanCompleteEvent {
  type: "scan_complete";
}

interface ErrorMessage {
  type: "error";
  message: string;
}

export interface SystemAlertEvent {
  event: string;
  message: string;
  level: "warning" | "critical" | "info";
  timestamp: number;
}

interface SystemAlertMessage {
  type: "system_alert";
  event: string;
  message: string;
  level: "warning" | "critical" | "info";
}

export interface StockTone {
  symbol: string; score: number; label: string; direction: string;
  spot: number; atm: number; pcr: number;
  ce_iv: number; pe_iv: number; iv_skew: number; straddle_pct: number;
  ce_ltp: number; pe_ltp: number; ce_oi: number; pe_oi: number;
  sector: string; tier: number;
}

export interface BnToneEvent {
  score: number;
  label: "Bullish" | "Neutral" | "Bearish";
  direction: "UP" | "NEUTRAL" | "DOWN";
  spot: number;
  atm: number;
  pcr: number | null;
  ce_iv: number;
  pe_iv: number;
  updated_at: string;
  reasoning: Record<string, unknown>;
  source: "premarket" | "live" | "init";
  strategies?: Record<string, number>;
}

interface BnToneMessage extends BnToneEvent {
  type: "bn_tone_update";
}

export interface PortfolioPnlEvent {
  realised_pnl:        number;
  unrealised_pnl:      number;
  gross_pnl:           number;
  net_pnl:             number;
  cash_balance:        number;
  wins_count:          number;
  losses_count:        number;
  trade_count:         number;
  open_position_count: number;
  positions?: Array<{
    symbol: string; quantity: number; avg_price: number;
    ltp: number; unrealised_pnl: number;
    instrument_type: string; product: string;
  }>;
  paper_orders?: Array<{
    order_id: string; symbol: string; side: string; quantity: number;
    price: number; strategy: string; slippage: number; timestamp: string | null;
  }>;
  paper_daily?: { realised_pnl: number; total_trades: number; wins: number; losses: number; total_margin_used?: number };
}

interface PortfolioPnlMessage extends PortfolioPnlEvent {
  type: "portfolio_pnl";
}

export interface IndicesStatusEvent {
  regime: { name: string; direction: string; adx: number; is_trending: boolean; lot_multiplier: number };
  vix: number;
  nifty_drop: number;
  crash_level: string;
  indices: Array<{
    id: string; label: string; color: string;
    auto_enabled: boolean; dir_enabled: boolean;
    dir_state: string;
    gates: Record<string, string>;
    tone: { score: number; label: string; direction: string; source: string; delta: number | null };
    feed: { spot_live: boolean; spot_price: number | null; change: number; change_pct: number; chain_ws: boolean; chain_ticking: boolean };
    pnl?: { trades: number; symbols: number; buy_value: number; sell_value: number; net_pnl: number };
    groups?: Array<{ group_id: string; strategy_name: string; net_pnl: number; pnl_pct: number; trail_phase: string; legs: number }>;
  }>;
  stock_futures?: Array<{ name: string; ltp: number | null; type: string; change: number | null; change_pct: number | null; prev_close: number | null }>;
}

interface IndicesStatusMessage extends IndicesStatusEvent {
  type: "indices_status";
}

export interface AppStatusEvent {
  auto_trade_enabled: boolean;
  bn_auto_enabled: boolean;
  nf_auto_enabled: boolean;
  fn_auto_enabled: boolean;
  mc_auto_enabled: boolean;
  crash_level: string;
  crash_protection_enabled: boolean;
  emergency_stop_active: boolean;
  paper_mode: boolean;
  daily_trade_count: number;
  max_daily_trades: number;
  bn_groups?: Array<{ group_id: string; strategy_name: string; net_pnl: number; pnl_pct: number }>;
  lm_state?: string;
  crash_protection_auto?: boolean;
}

interface AppStatusMessage extends AppStatusEvent {
  type: "app_status";
}

interface StockTonesMessage { type: "stock_tones"; stocks: Record<string, StockTone>; }

interface PingMessage { type: "ping"; }

export interface ScanMonitorEvent {
  market: {
    time: string; system_ready: boolean; scan_running: boolean;
    vix: number; adx: number; regime: string; direction: string;
    is_trending: boolean; nifty_drop: number; crash_level: string;
    daily_loss_halt: boolean; daily_trade_count: number;
    breadth: { advances: number; declines: number; ratio: number };
  };
  scan: {
    total_liquid: number; auto_executed: number; queued: number;
    hold: number; score_low: number; time_miss: number;
    profit_low: number; duplicate: number;
    near_misses: Array<{ symbol: string; score: number; reason: string }>;
  };
  indices: Array<{ name: string; spot: number | null; tone: number; tone_label: string; tone_source: string }>;
  stock_futures: Array<{ name: string; ltp: number | null; type: string; change: number | null; change_pct: number | null; prev_close: number | null }>;
  ai_strategies: Array<{
    instrument: string; strategy: string; side: string;
    triggered: boolean; confidence: number;
    conditions_met: number; conditions_total: number;
  }>;
  ai_positions: Array<{ symbol: string; strategy: string; side: string; entry_price: number; quantity: number }>;
  ai_entries_today: number;
  top_screened: Array<{ symbol: string; score: number; strategy: string; side: string; reason: string }>;
  sectors: { counts: Record<string, number>; top: string[]; bottom: string[] };
  recording: { active: boolean; instruments: number; path: string };
}

interface ScanMonitorMessage extends ScanMonitorEvent { type: "scan_monitor"; }

interface ChainTickMessage { type: "chain_tick"; index: string; data: Array<{ symbol: string; ltp: number; oi?: number }>; }
interface ChainSubscribedMessage { type: "chain_subscribed"; index: string; }
interface OrderUpdateMessage { type: "order_update"; symbol: string; event: string; is_close: boolean; strategy?: string; }

type WsMessage = TickMessage | SubscribedMessage | AutoCloseMessage | HoldConvertMessage | ScanCompleteMessage | ErrorMessage | SystemAlertMessage | StockTonesMessage | BnToneMessage | PortfolioPnlMessage | IndicesStatusMessage | AppStatusMessage | ScanMonitorMessage | PingMessage | ChainTickMessage | ChainSubscribedMessage | OrderUpdateMessage;

const getWsUrl = () => {
  // Safari private mode / sandboxed iframes can throw SecurityError on
  // localStorage access. Catch defensively so a browser-level storage
  // block doesn't kill the WebSocket connection silently — instead, the
  // connection proceeds unauthenticated and the backend's token gate
  // returns a clean 401 which the reconnect loop surfaces.
  let token = "";
  try {
    token = localStorage.getItem("mct_token") ?? "";
  } catch (e) {
    console.warn("[WS] localStorage unavailable:", e);
  }
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? "/api";
  let wsBase: string;
  if (apiBase.startsWith("http")) {
    // Absolute URL (dev mode): http://localhost:8000/api → ws://localhost:8000
    wsBase = apiBase.replace(/^http:/, "ws:").replace(/^https:/, "wss:").replace(/\/api$/, "");
  } else {
    // Relative URL (prod mode): /api → wss://current-host
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    wsBase = `${proto}//${window.location.host}`;
  }
  return `${wsBase}/ws/ticks?token=${encodeURIComponent(token)}`;
};
const RECONNECT_BASE_MS = 5000;
const RECONNECT_MAX_MS = 60000;
const RECONNECT_MAX_ATTEMPTS = 50;

export function useLiveTicks() {
  // 2026-04-27 perf: ltpMap holds POSITION LTPs (from `tick` messages).
  // Index spot prices live in indicesLtpMap (from `indices_status`) so
  // a position tick doesn't churn IndexStatusPanel and vice-versa.
  // Components consuming index spots should use `indicesLtpMap`; everything
  // else keeps using `ltpMap`. Backwards-compat: `ltpMap` still gets index
  // entries written into it on indices_status (legacy callers depend on it).
  const [ltpMap, setLtpMap] = useState<Record<string, number>>({});
  const [indicesLtpMap, setIndicesLtpMap] = useState<Record<string, number>>({});
  const [predMap, setPredMap] = useState<Record<string, TickEntry>>({});
  const [autoCloseEvents, setAutoCloseEvents] = useState<AutoCloseEvent[]>([]);
  const [scanComplete, setScanComplete] = useState<ScanCompleteEvent | null>(null);
  const [systemAlerts, setSystemAlerts] = useState<SystemAlertEvent[]>([]);
  const [stockTones, setStockTones] = useState<Record<string, StockTone>>({});
  const [bnTone, setBnTone] = useState<BnToneEvent | null>(null);
  const [livePnl, setLivePnl] = useState<PortfolioPnlEvent | null>(null);
  const [indicesStatus, setIndicesStatus] = useState<IndicesStatusEvent | null>(null);
  const [appStatus, setAppStatus] = useState<AppStatusEvent | null>(null);
  const [scanMonitor, setScanMonitor] = useState<ScanMonitorEvent | null>(null);
  // chain state: per-index LTP maps populated by chain_tick WS messages
  const [chainTicks, setChainTicks] = useState<Record<string, Record<string, number>>>({});
  const [chainSubscribed, setChainSubscribed] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<TickStatus>("connecting");
  const [subscribedSymbols, setSubscribedSymbols] = useState<string[]>([]);

  const qc = useQueryClient();
  const queryClientRef = useRef(qc);
  queryClientRef.current = qc;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const reconnectAttempts = useRef(0);

  // 2026-04-29 (UI-jitter incident): tick coalescing buffer.
  // Without this, every tick (3-5/sec/symbol × 8 positions = 25-40/sec)
  // triggered a setLtpMap which re-rendered every component subscribed
  // to ltpMap. Active Positions cards visibly jittered as the +/− sign
  // and color flipped on tick noise. Now: accumulate ticks in a ref,
  // flush via a single setState every LTP_FLUSH_MS. Visual update rate
  // bounded; no semantic loss (we still see the latest LTP within
  // ~250ms which is well under any operator decision latency).
  const ltpBufferRef = useRef<Record<string, number>>({});
  const ltpFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const predBufferRef = useRef<Record<string, TickEntry>>({});
  const LTP_FLUSH_MS = 250;

  const _scheduleLtpFlush = () => {
    if (ltpFlushTimerRef.current !== null) return;
    ltpFlushTimerRef.current = setTimeout(() => {
      ltpFlushTimerRef.current = null;
      const buffered = ltpBufferRef.current;
      const bufferedPred = predBufferRef.current;
      if (Object.keys(buffered).length > 0) {
        ltpBufferRef.current = {};
        setLtpMap(prev => ({ ...prev, ...buffered }));
      }
      if (Object.keys(bufferedPred).length > 0) {
        predBufferRef.current = {};
        setPredMap(prev => ({ ...prev, ...bufferedPred }));
      }
    }, LTP_FLUSH_MS);
  };
  const lastMessageTs = useRef<number>(Date.now());
  const heartbeatInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  // Fix 19: mirror chainSubscribed into a ref so `connect`'s onopen handler
  // can read the latest value after reconnect. connect is wrapped in
  // useCallback([]) with no deps, so state captured by closure would be
  // stale. The backend creates a fresh session per WebSocket and only
  // auto-subscribes to position tokens — chain subscriptions need to be
  // re-sent explicitly after reconnect or the chain UI silently goes cold.
  const chainSubscribedRef = useRef<Record<string, boolean>>({});
  useEffect(() => {
    chainSubscribedRef.current = chainSubscribed;
  }, [chainSubscribed]);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;

    // Clean up any existing connection
    if (wsRef.current && wsRef.current.readyState < WebSocket.CLOSING) {
      wsRef.current.close();
    }

    setStatus("connecting");
    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      // Phase E: re-bootstrap state-store on reconnect. If this is the
      // FIRST connection of the session reconnectAttempts is already 0
      // (initial mount); skip the bootstrap (App.tsx already fired one).
      // For subsequent reconnects, re-fire to fill any gap.
      if (reconnectAttempts.current > 0) {
        void onStateChannelReconnect();
      }
      reconnectAttempts.current = 0;
      // Fix 19: re-send every previously-active chain subscription so the
      // new session on the backend knows which chains we want. Without this
      // the chain UI goes silent after any reconnect because the backend
      // only auto-subscribes to position tokens on a fresh session.
      const prior = chainSubscribedRef.current;
      for (const [idx, isSub] of Object.entries(prior)) {
        if (isSub && ws.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify({ type: "subscribe_chain", index: idx, expiry: "" }));
          } catch (e) {
            console.error("[WS] chain resubscribe failed for", idx, e);
          }
        }
      }
      // status is set to "live" only after we receive a "subscribed" message
    };

    ws.onmessage = (event: MessageEvent) => {
      if (!mountedRef.current) return;
      lastMessageTs.current = Date.now();
      try {
        const msg: WsMessage = JSON.parse(event.data);

        // 2026-04-23 ADR-012 Phase 1: route state-channel messages
        // (state_init / state_delta) into the Zustand store. Returns true
        // when handled — short-circuit the rest of the dispatch in that case.
        // Static import (resolved at module load) — store is always present;
        // the per-component opt-in (isStateStoreEnabled) controls whether
        // any component actually CONSUMES the store data.
        if (handleStateMessage(msg)) return;

        if (msg.type === "tick") {
          // 2026-04-29 UI-jitter fix: coalesce into ref, flush every 250ms.
          // Eliminates the per-tick setState storm that made Active Positions
          // cards visibly jitter (sign + color flip on tick noise).
          for (const entry of msg.data) {
            ltpBufferRef.current[entry.symbol] = entry.ltp;
            if (entry.direction !== undefined) {
              predBufferRef.current[entry.symbol] = entry;
            }
          }
          _scheduleLtpFlush();
        } else if (msg.type === "subscribed") {
          setStatus("live");
          setSubscribedSymbols(msg.symbols ?? []);
        } else if (msg.type === "auto_close") {
          setAutoCloseEvents(prev => [
            { ...msg, timestamp: Date.now() },
            ...prev.slice(0, 9),
          ]);
        } else if (msg.type === "hold_convert") {
          // Treat hold conversions as system alerts so they appear in the alert bar
          setSystemAlerts(prev => [
            {
              event: "hold_convert",
              message: `${msg.symbol}: Converted MIS→${msg.product} near SL (${msg.pnl_pct.toFixed(1)}%) — ${msg.reason}`,
              level: "warning",
              timestamp: Date.now(),
            },
            ...prev.slice(0, 4),
          ]);
        } else if (msg.type === "scan_complete") {
          const { type: _, ...event } = msg;
          setScanComplete(event);
        } else if (msg.type === "system_alert") {
          const alertTs = Date.now();
          setSystemAlerts(prev => {
            // Deduplicate: skip if the same message was already received within 30s
            const isDuplicate = prev.some(
              a => a.message === msg.message && alertTs - a.timestamp < 30_000
            );
            if (isDuplicate) return prev;
            return [
              { event: msg.event, message: msg.message, level: msg.level, timestamp: alertTs },
              ...prev.slice(0, 4),  // keep last 5 alerts
            ];
          });
          // Auto-dismiss handled by tradingStore.addSystemAlert (20s timeout)
        } else if (msg.type === "stock_tones") {
          setStockTones(msg.stocks ?? {});
        } else if (msg.type === "bn_tone_update") {
          const { type: _, ...tone } = msg;
          setBnTone(tone as BnToneEvent);
        } else if (msg.type === "portfolio_pnl") {
          const { type: _, ...pnl } = msg;
          setLivePnl(pnl as PortfolioPnlEvent);
        } else if (msg.type === "indices_status") {
          const { type: _, ...data } = msg;
          setIndicesStatus(data as IndicesStatusEvent);
          // 2026-04-27 perf: index spots go to `indicesLtpMap` ONLY (not
          // `ltpMap`). Position-display components subscribed to `ltpMap`
          // no longer re-render on every index tick. The 3 consumers that
          // read index spots (IndexStatusPanel, MarketsPage) read from
          // `indicesLtpMap` — verified by repo grep at change time.
          setIndicesLtpMap(prev => {
            const next = { ...prev };
            for (const idx of data.indices) {
              if (idx.feed.spot_price) next[idx.id] = idx.feed.spot_price;
            }
            return next;
          });
        } else if (msg.type === "app_status") {
          const { type: _, ...data } = msg;
          setAppStatus(data as AppStatusEvent);
        } else if (msg.type === "scan_monitor") {
          const { type: _, ...data } = msg;
          setScanMonitor(data as ScanMonitorEvent);
        } else if (msg.type === "order_update") {
          // Instant refresh of orders + positions on any fill/close/reject
          // — eliminates 30–60s REST polling gap
          try {
            queryClientRef.current?.invalidateQueries({ queryKey: ["orders"] });
            queryClientRef.current?.invalidateQueries({ queryKey: ["portfolio"] });
            queryClientRef.current?.invalidateQueries({ queryKey: ["closed-positions"] });
            if (msg.is_close) {
              queryClientRef.current?.invalidateQueries({ queryKey: ["paperPositions"] });
              queryClientRef.current?.invalidateQueries({ queryKey: ["paperOrders"] });
            }
          } catch { /* non-critical */ }
        } else if (msg.type === "chain_tick") {
          const { index: idx, data: ticks } = msg;
          if (idx && ticks?.length) {
            setChainTicks(prev => {
              const prevIdx = prev[idx] ?? {};
              const next: Record<string, number> = { ...prevIdx };
              for (const { symbol, ltp } of ticks) next[symbol] = ltp;
              return { ...prev, [idx]: next };
            });
          }
        } else if (msg.type === "chain_subscribed") {
          const { index: idx } = msg;
          if (idx) setChainSubscribed(prev => ({ ...prev, [idx]: true }));
        } else if (msg.type === "ping") {
          // Keepalive from server — respond with pong
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: "pong" }));
          }
        } else if (msg.type === "error") {
          setStatus("error");
        }
      } catch (err) {
        // Fix 24: surface malformed messages instead of swallowing them.
        // A truncated or corrupted payload from the backend used to be
        // invisible here, so "chain tab goes silent" and other stall
        // symptoms had no trail in devtools. Log just enough to point
        // the operator at the message that broke.
        const preview = typeof event.data === "string" ? event.data.slice(0, 200) : "<non-string>";
        console.error("[WS] parse error:", err, "payload preview:", preview);
      }
    };

    // Heartbeat timeout: if no message received in 35s, assume connection is dead
    if (heartbeatInterval.current) clearInterval(heartbeatInterval.current);
    heartbeatInterval.current = setInterval(() => {
      if (Date.now() - lastMessageTs.current > 35_000 && ws.readyState === WebSocket.OPEN) {
        console.warn("[WS] heartbeat timeout — no message in 35s, forcing reconnect");
        ws.close();
      }
    }, 5_000);

    ws.onerror = () => {
      if (!mountedRef.current) return;
      setStatus("error");
    };

    ws.onclose = (event) => {
      if (!mountedRef.current) return;
      reconnectAttempts.current += 1;
      // ADR-012 Phase 1: mark state-aggregator slices stale on disconnect.
      onStateChannelDisconnect();
      if (reconnectAttempts.current >= RECONNECT_MAX_ATTEMPTS) {
        setStatus("error");
        return;
      }
      setStatus("closed");
      // 2026-04-27: backend WS rate-limit rejects with code 4408 ("WS open
      // rate limit exceeded"). Without special handling, normal exponential
      // backoff (5s → 10s) keeps hitting the limit → infinite reconnect
      // storm visible as `/api/state/bootstrap` firing every 3s. When we
      // see 4408, jump straight to the max backoff (60s) so the rate-limit
      // window can drain.
      const isRateLimited = event?.code === 4408;
      let baseDelay: number;
      if (isRateLimited) {
        baseDelay = RECONNECT_MAX_MS;  // 60s — let the rate-limit window drain
        console.warn(`[WS] rate-limited by backend (code 4408) — backing off ${baseDelay/1000}s`);
      } else {
        // Exponential backoff: 5s → 10s → 20s → 40s → 60s (capped).
        baseDelay = Math.min(
          RECONNECT_BASE_MS * Math.pow(2, reconnectAttempts.current - 1),
          RECONNECT_MAX_MS,
        );
      }
      // Fix 25: add up to 1 s of jitter to the computed delay so multiple
      // clients reconnecting after a shared backend restart don't thunder
      // in at the same instant.
      const jitter = Math.random() * 1000;
      const delay = baseDelay + jitter;
      reconnectTimer.current = setTimeout(() => {
        if (mountedRef.current) connect();
      }, delay);
    };
  }, []);

  // Instant reconnect when screen unlocked / tab becomes visible again.
  // Browsers suspend WebSocket and setInterval when tab is backgrounded or
  // screen is locked. Without this, the UI freezes for 30-60s after unlock
  // while the heartbeat timeout slowly detects the dead connection.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && mountedRef.current) {
        const ws = wsRef.current;
        const staleSec = (Date.now() - lastMessageTs.current) / 1000;
        // If last message was >5s ago, connection is likely dead — reconnect immediately
        if (!ws || ws.readyState !== WebSocket.OPEN || staleSec > 5) {
          console.info(`[WS] Tab visible — stale ${staleSec.toFixed(0)}s, reconnecting now`);
          if (ws && ws.readyState < WebSocket.CLOSING) ws.close();
          connect();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
        heartbeatInterval.current = null;
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      // 2026-04-29 UI-jitter fix: cancel pending LTP flush on unmount.
      if (ltpFlushTimerRef.current) {
        clearTimeout(ltpFlushTimerRef.current);
        ltpFlushTimerRef.current = null;
      }
      ltpBufferRef.current = {};
      predBufferRef.current = {};
      if (wsRef.current) {
        // Remove handlers before closing to prevent onclose from scheduling reconnect
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        if (wsRef.current.readyState !== WebSocket.CLOSED) {
          wsRef.current.close();
        }
        wsRef.current = null;
      }
      // Reset all state on unmount
      setLtpMap({});
      setIndicesLtpMap({});
      setPredMap({});
      setAutoCloseEvents([]);
      setScanComplete(null);
      setSystemAlerts([]);
      setStockTones({});
      setBnTone(null);
      setLivePnl(null);
      setIndicesStatus(null);
      setAppStatus(null);
      setStatus("closed");
      setSubscribedSymbols([]);
      reconnectAttempts.current = 0;
    };
  }, [connect]);

  /** Call this after approving a new signal so the server re-subscribes. */
  const refresh = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "refresh" }));
    }
  }, []);

  /** Subscribe extra symbols for live LTP ticks (e.g. pending signals). No predictor side-effects. */
  const watch = useCallback((symbols: string[]) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && symbols.length > 0) {
      wsRef.current.send(JSON.stringify({ type: "watch", symbols }));
    }
  }, []);

  const dismissAlert = useCallback((timestamp: number) => {
    setSystemAlerts(prev => prev.filter(a => a.timestamp !== timestamp));
  }, []);

  const subscribeChain = useCallback((index: string, expiry?: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "subscribe_chain", index, expiry: expiry ?? "" }));
    }
  }, []);

  const unsubscribeChain = useCallback((index: string) => {
    setChainSubscribed(prev => ({ ...prev, [index]: false }));
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "unsubscribe_chain", index }));
    }
  }, []);

  return { ltpMap, indicesLtpMap, predMap, autoCloseEvents, scanComplete, systemAlerts, dismissAlert, stockTones, bnTone, livePnl, indicesStatus, appStatus, scanMonitor, chainTicks, chainSubscribed, subscribeChain, unsubscribeChain, status, subscribedSymbols, refresh, watch };
}
